import { RoleType, Permission, getPermissionsForRole } from '../config/roles';
import { getApiBaseUrl } from '../utils/environment';

/**
 * Authentication provider enum
 */
export enum AuthProvider {
  DISCORD = 'discord',
  GOOGLE = 'google'
}

/**
 * User information interface
 */
export interface UserInfo {
  id: string;
  email: string;
  username: string;
  avatar: string | null;
  provider: AuthProvider;
  roles: RoleType[];
  permissions: Permission[];
}

/**
 * Manages interaction with the backend for user authentication and session status.
 * Relies on HttpOnly session cookies managed by the backend.
 */
export class AuthenticationManager {
  private static readonly USER_INFO_KEY = 'mythbound_user_info';

  private userInfo: UserInfo | null = null;

  // Event listeners
  private eventListeners: { [key: string]: Array<Function> } = {
    'auth:login': [],
    'auth:logout': [],
    'auth:role-change': [],
  };

  constructor() {}

  // Event handling methods
  addEventListener(event: string, callback: Function): void {
    if (this.eventListeners[event]) {
      this.eventListeners[event].push(callback);
    }
  }

  removeEventListener(event: string, callback: Function): void {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  private triggerEvent(event: string, data?: any): void {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} event listener:`, error);
        }
      });
    }

    // Also dispatch DOM event for components to listen to
    window.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Initiates the login flow by redirecting the user to the backend
   * endpoint for the specified provider.
   * @param provider The authentication provider (Discord or Google).
   */
  initiateLogin(provider: AuthProvider): void {
    const apiBase = this.getApiBaseUrl();
    const providerString = provider === AuthProvider.DISCORD ? 'discord' : 'google';
    const loginUrl = `${apiBase}/auth/login/${providerString}`;
    console.log(`Redirecting to ${providerString} login: ${loginUrl}`);
    // Redirect the browser to the backend login endpoint
    window.location.href = loginUrl;
  }

  /**
   * Checks the current session status by calling the backend.
   * Updates the internal userInfo state based on the response.
   * Relies on the browser sending the HttpOnly session cookie automatically.
   * @returns A Promise resolving to the UserInfo if authenticated, or null otherwise.
   */
  async checkSessionStatus(): Promise<UserInfo | null> {
    const apiBase = this.getApiBaseUrl();
    const statusUrl = `${apiBase}/auth/session`;

    try {
      const response = await fetch(statusUrl, {
        method: 'GET',
        // credentials: 'include' ensures cookies (like the session cookie) are sent
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.isAuthenticated && data.user) {
          console.debug('[Auth] Session valid. User:', data.user.username);
          this.setUserInfo(data.user);
          this.triggerEvent('auth:login', data.user);
          return data.user;
        } else {
          console.debug('[Auth] Session invalid or user data missing.');
          this.clearUserInfo();
          return null;
        }
      } else if (response.status === 401) { // Unauthorized
        console.debug('[Auth] No active session found (401).');
        this.clearUserInfo();
        return null;
      } else {
        // Handle other non-OK statuses (e.g., 500 server error)
        console.error(`[Auth] Session check failed with status: ${response.status}`);
        this.clearUserInfo();
        return null;
      }
    } catch (error) {
      console.error('[Auth] Network or other error during session check:', error);
      this.clearUserInfo();
      return null;
    }
  }

  /**
   * Get current user info
   */
  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: RoleType): boolean {
    return this.userInfo?.roles.includes(role) || false;
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: Permission): boolean {
    return this.userInfo?.permissions.includes(permission) || false;
  }

  /**
   * Store user info in memory (and optionally localStorage for caching)
   */
  private setUserInfo(userInfo: UserInfo): void {
    this.userInfo = userInfo;

    // Optionally cache in localStorage, but checkSessionStatus is the source of truth
    localStorage.setItem(AuthenticationManager.USER_INFO_KEY, JSON.stringify(userInfo));

    // Trigger role change event if user info changed
    this.triggerEvent('auth:role-change', userInfo);
  }

  /**
   * Clear user info from memory and storage
   */
  private clearUserInfo(): void {
    const wasLoggedIn = !!this.userInfo;
    this.userInfo = null;

    localStorage.removeItem(AuthenticationManager.USER_INFO_KEY);

    if (wasLoggedIn) {
      // Trigger logout event only if user was previously considered logged in
      this.triggerEvent('auth:logout');
    }
  }

  /**
   * Get the appropriate API base URL based on the current environment
   * This handles both development and production environments
   */
  private getApiBaseUrl(): string {
    return getApiBaseUrl();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    // User is considered authenticated if we have user info from a successful session check
    return !!this.userInfo;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    const apiBase = this.getApiBaseUrl();
    const endpoint = `${apiBase}/auth/logout`;

    try {
      await fetch(endpoint, {
        method: 'POST',
        credentials: 'include', // Send session cookie
        headers: {
          // No Authorization header needed, rely on cookie
          'Accept': 'application/json',
        },
      });
      console.debug('[Auth] Logout request sent to backend.');
    } catch (error) {
      console.error('[Auth] Error sending logout request to backend:', error);
      // Proceed with client-side logout regardless of server communication error
    }

    // Always clear local user info regardless of server response
    this.clearUserInfo();
  }
}